import { useEffect, useState } from "react";
import { Flex, Text } from "@chakra-ui/react";
import { ContentBlock } from "@/components/ContentBlock/ContentBlock";
import { ContentGroup } from "@/components/ContentGroup/ContentGroup";
import { Disclaimer } from "@/components/Disclaimer/Disclaimer";
import DocumentCollection from "@/components/DocumentCollection/DocumentCollection";
import type { DocumentCollectionProps } from "@/components/DocumentCollection/DocumentCollectionFundList";
import { useInstanceConfig } from "@/services/InstanceConfigContext";
import { GET } from "@/services/api/client";
import { envVar } from "@/services/env";
import { layoutEmitter } from "@/services/layoutUtils";
import { useHybridRouter } from "@/services/router/useHybridRouter";
import type { ShareClassDashboard } from "@/services/server/v1/getShareClassDashboard";
import { useHtmlifiedTranslations } from "@/services/useHtmlifiedTranslations";
import { htmlify, interpolatePlaceholders } from "@/services/utils";
import type { FundDetailPagePathProps } from "@/types/FundDetailPagePathProps";
import type { GetDocumentCategories } from "@/types/endpoints/GetDocumentCategoriesEndpoint";

interface FxDocumentGroupPanelProps {
	dashboard: ShareClassDashboard;
}

export const FxDocumentGroupPanel = ({ dashboard }: FxDocumentGroupPanelProps) => {
	const { fxDocumentGroupPanel: props, translations: encodedTranslations, shareClass } = dashboard;
	const { documents: originalDocuments, useExternalSource, allowBulkDownload, externalPayload, disclaimer } = props || {};
	const translations = useHtmlifiedTranslations(encodedTranslations);
	const [documentGroups, setDocumentGroups] = useState(useExternalSource ? undefined : originalDocuments);
	const [loading, setLoading] = useState(useExternalSource);
	const instanceConfig = useInstanceConfig();

	const {
		query: { location, channel, language, preferredLanguages }
	} = useHybridRouter<FundDetailPagePathProps>();

	useEffect(() => {
		if (!loading) {
			return;
		}

		const callGetDocumentCategories = async () => {
			try {
				const languages = preferredLanguages
					? JSON.parse(preferredLanguages).filter((item: string) => {
							return item !== "all" && item !== "debug";
						})
					: [];

				const response = await GET<GetDocumentCategories>(`${envVar("PUBLIC_ENV__API_HOST")}/api/v1/getDocumentCategories`, {
					location,
					channel,
					language: languages.length > 0 ? languages[0] : language,
					...(shareClass as any),
					...externalPayload
				});
				setLoading(false);

				if (response.isErr) {
					setDocumentGroups([]);
					return;
				}

				const { documentGroups } = response.body.data;
				const newDocumentGroups = documentGroups.length > 0 ? documentGroups : undefined;

				setDocumentGroups(newDocumentGroups);

				if (newDocumentGroups) {
					layoutEmitter.emit("SectionRendered", "#fxDocumentGroupPanel");
				}
			} catch (e) {
				console.error(e);
				setDocumentGroups(undefined);
				setLoading(false);
			}
		};

		void callGetDocumentCategories();
	}, [shareClass, location, channel, language, loading, externalPayload, preferredLanguages]);

	const options = {
		shareClassExternalId: shareClass.externalId
	};

	if (!props || !documentGroups) {
		return null;
	}

	return (
		<ContentGroup id="fxDocumentGroupPanel" data-testid="FxDocumentGroupPanel" tabIndex={-1} shouldApplySmallScreenPadding={false}>
			<Flex flexDirection="column" gap={2} flex={1}>
				<ContentBlock heading={translations?.documentsTitle} tooltip={translations?.documentsTitleTooltip}>
					{translations?.documentPanelDescriptionDisclaimer && (
						<Text fontSize="16px">{translations.documentPanelDescriptionDisclaimer}</Text>
					)}
					{translations?.documentPanelDisclaimer && (
						<Text fontSize="16px" p={{ color: "inherit" }}>
							{translations.documentPanelDisclaimer}
						</Text>
					)}
					<Flex flexDirection="column" gap="38px">
						{documentGroups.map((items: DocumentCollectionProps, index) => {
							return (
								<DocumentCollection
									key={index}
									allowBulkDownload={allowBulkDownload}
									{...items}
									options={options}
									translations={translations}
								/>
							);
						})}
					</Flex>
				</ContentBlock>
				{Boolean(disclaimer) && <Disclaimer>{htmlify(interpolatePlaceholders({ text: disclaimer, instanceConfig }))}</Disclaimer>}
			</Flex>
		</ContentGroup>
	);
};
